import { Alert, AlertBody, AlertIcon, Checkbox, RiInformationLine, Spinner, Stack } from 'component-library';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { SuspenseBoundary } from '@/components';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { useOrganizationPlotGroups } from '@/pages/shared/hooks/useOrganizationPlotGroup';

import { useWorkstreamCreateContext, WORKSTREAM_FORM_KEYS } from '../hooks/useFormControls';

export const WorkstreamFormPlotGroups = ({ form }: { form: ReturnType<typeof useWorkstreamCreateContext> }) => (
  <ErrorBoundary FallbackComponent={() => null}>
    <SuspenseBoundary
      fallback={
        <Stack centerMain>
          <Spinner size={10} />
        </Stack>
      }
    >
      <WorkstreamFormPlotGroupsBase form={form} />
    </SuspenseBoundary>
  </ErrorBoundary>
);

const WorkstreamFormPlotGroupsBase = ({ form }: { form: ReturnType<typeof useWorkstreamCreateContext> }) => {
  const { t } = useTranslation();
  const orgId = useOrganizationId();
  const plotGroups = useOrganizationPlotGroups(orgId).data;
  const selectedIds: string[] = form.watch(WORKSTREAM_FORM_KEYS.PLOT_GROUPS) ?? [];

  const togglePlotGroup = (id: string) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((selectedId) => selectedId !== id)
      : [...selectedIds, id];
    form.setValue(WORKSTREAM_FORM_KEYS.PLOT_GROUPS, next, { shouldDirty: true });
  };

  return (
    <Stack spacing={4} className='rounded-lg border border-divider p-6'>
      <Stack direction='row' centerMain className='justify-between'>
        <span className='typography-h3 text-text-primary'>{t('global.workstream.labels.plotGroups')}</span>
        <span className='typography-caption text-text-secondary'>
          {t('global.workstream.labels.groupsAssigned', {
            selected: selectedIds.length,
            total: plotGroups.length,
          })}
        </span>
      </Stack>
      {selectedIds.length === 0 && (
        <Alert>
          <AlertIcon>
            <RiInformationLine size={20} />
          </AlertIcon>
          <AlertBody>{t('global.workstream.labels.allPlotsAssignedHint')}</AlertBody>
        </Alert>
      )}
      <Stack spacing={2}>
        {plotGroups.map((plotGroup) => (
          <Stack
            key={plotGroup.id}
            direction='row'
            centerMain
            className='justify-between rounded-lg border border-divider px-4 py-3'
          >
            <Stack direction='row' spacing={3} centerMain>
              <Checkbox
                checked={selectedIds.includes(plotGroup.id)}
                onCheckedChange={() => togglePlotGroup(plotGroup.id)}
              />
              <span className='typography-body1 text-text-primary'>{plotGroup.name}</span>
            </Stack>
            <span className='typography-caption text-text-secondary'>
              {t('global.workstream.labels.plotCount', { count: plotGroup.total_plots ?? 0 })}
            </span>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
